/** 与历史会话并列时保持紧凑，避免应用列表抢占侧栏。 */
export const SIDEBAR_APP_LIST_LIMIT = 5
/** 没有历史会话区域时利用空余空间展示更多应用。 */
export const SIDEBAR_APP_LIST_EXPANDED_LIMIT = 10
export const MOBILE_DRAWER_APP_LIST_LIMIT = 15

interface NavigationApp {
  id: string
  is_favorite: boolean
  name: string
}

const navigationNameCollator = new Intl.Collator('zh-CN-u-co-pinyin', {
  numeric: true,
  sensitivity: 'base',
})

function normalizedNavigationName(name: string) {
  return name.trim().normalize('NFC')
}

/** 按收藏、完整拼音名称和稳定应用 ID 比较导航应用。 */
export function compareNavigationApps<T extends NavigationApp>(left: T, right: T) {
  const favoriteOrder = Number(right.is_favorite) - Number(left.is_favorite)

  if (favoriteOrder !== 0) {
    return favoriteOrder
  }

  const nameOrder = navigationNameCollator.compare(
    normalizedNavigationName(left.name),
    normalizedNavigationName(right.name),
  )

  if (nameOrder !== 0) {
    return nameOrder
  }

  return left.id < right.id ? -1 : left.id > right.id ? 1 : 0
}

/** 返回新的稳定排序数组，不修改 API 或 query cache 中的原始集合。 */
export function sortNavigationApps<T extends NavigationApp>(apps: readonly T[]) {
  return [...apps].sort(compareNavigationApps)
}

/**
 * 「我的应用」列表顺序：仅把收藏提到前面，其余保持接口返回顺序。
 * 侧栏使用拼音排序，两者语义不同，因此默认跳转必须使用本函数保持与页面一致。
 */
export function sortMyAppsOrder<T extends { is_favorite: boolean }>(apps: readonly T[]) {
  return [...apps].sort((left, right) => Number(right.is_favorite) - Number(left.is_favorite))
}

/**
 * 从完整排序集合中选出侧栏可见项；越界 current app 会替换最低排名项后重新排序。
 */
export function selectVisibleNavigationApps<T extends NavigationApp>(
  sortedApps: readonly T[],
  currentAppId?: string,
  limit = SIDEBAR_APP_LIST_LIMIT,
) {
  if (limit <= 0) {
    return []
  }

  const visibleApps = sortedApps.slice(0, limit)
  const normalizedCurrentAppId = currentAppId?.trim()

  if (!normalizedCurrentAppId || visibleApps.some((app) => app.id === normalizedCurrentAppId)) {
    return visibleApps
  }

  const currentApp = sortedApps.find((app) => app.id === normalizedCurrentAppId)

  if (!currentApp) {
    return visibleApps
  }

  return [...visibleApps.slice(0, -1), currentApp].sort(compareNavigationApps)
}
